// src/components/common/ResponsiveImage.jsx
//
// ONE component for every image in the app (facility pages, home, gallery,
// blog, about, nav/footer logos, etc). It always renders a <picture> with
// two candidates:
//   - a MOBILE-sized source (used at viewport widths <= 767px)
//   - a DESKTOP-sized source (used otherwise, and as the fallback <img>)
//
// It supports two kinds of images:
//
// 1) REMOTE images (Cloudinary / Unsplash URLs, e.g. facility photos an
//    admin uploaded). Pass a single `src` string - the mobile/desktop
//    variants are generated automatically via optimizeImageUrl().
//
//      <ResponsiveImage src={facility.bgImage} alt="..." eager />
//
// 2) LOCAL bundled assets (src/assets/*). Vite can't resize a string at
//    runtime, so the two sizes must be produced at build time with
//    vite-imagetools and passed in explicitly:
//
//      import bgDesktop from "../../assets/bg1.jpg?w=1920&format=webp&quality=75";
//      import bgMobile  from "../../assets/bg1.jpg?w=800&format=webp&quality=70";
//      <ResponsiveImage desktopSrc={bgDesktop} mobileSrc={bgMobile} alt="..." />
//
// Native lazy-loading, async decode, and a fade-in on load are handled
// centrally here so no component needs to reimplement them.

import { useState } from "react";
import { getResponsiveSources, getSrcSet, BREAKPOINTS } from "../../utils/imageOptimize";

const MOBILE_BREAKPOINT = "(max-width: 767px)";

/**
 * @param {string}  [src]         Remote CDN URL (auto-optimized for mobile/desktop)
 * @param {string}  [mobileSrc]   Pre-built local asset for mobile (overrides auto)
 * @param {string}  [desktopSrc]  Pre-built local asset for desktop (overrides auto)
 * @param {string}  alt
 * @param {boolean} [eager]       true for above-the-fold/hero images
 * @param {number}  [mobileWidth]  only used when deriving from `src`
 * @param {number}  [desktopWidth] only used when deriving from `src` - set this
 *                                 to roughly the image's real max rendered
 *                                 width (e.g. 400 for a grid tile, 1920 for a
 *                                 full-bleed hero) so the browser isn't
 *                                 offered candidates bigger than it needs.
 * @param {string}  [sizes]       CSS `sizes` attribute describing how wide the
 *                                 image actually renders at each breakpoint,
 *                                 e.g. "(max-width: 767px) 100vw, 400px".
 *                                 Defaults to a sensible guess based on `eager`.
 * @param {string}  [className]
 * @param {object}  [style]
 */
const ResponsiveImage = ({
  src,
  mobileSrc,
  desktopSrc,
  alt = "",
  eager = false,
  mobileWidth = BREAKPOINTS.mobile,
  desktopWidth = BREAKPOINTS.desktop,
  sizes,
  className = "",
  style,
  ...rest
}) => {
  const [loaded, setLoaded] = useState(false);

  // Local, pre-built variants take priority (explicit props win). Otherwise,
  // if we were given a plain remote URL, derive both sizes automatically.
  const hasExplicitVariants = Boolean(mobileSrc || desktopSrc);
  const { mobile, desktop } = hasExplicitVariants
    ? { mobile: mobileSrc || desktopSrc, desktop: desktopSrc || mobileSrc }
    : getResponsiveSources(src, { mobileWidth, desktopWidth });

  const fallback = desktop || mobile;
  if (!fallback) return null;

  // Local imagetools assets are already a single fixed file per breakpoint -
  // no srcset needed. Remote CDN images get a full srcset so the browser can
  // request exactly the resolution it needs instead of always the max width.
  const srcSet = !hasExplicitVariants && src ? getSrcSet(src, { maxWidth: desktopWidth }) : undefined;
  const resolvedSizes = sizes || (eager ? "100vw" : `(max-width: 767px) 100vw, ${desktopWidth}px`);

  return (
    <picture>
      {mobile && mobile !== desktop && (
        <source media={MOBILE_BREAKPOINT} srcSet={mobile} />
      )}
      <img
        src={fallback}
        srcSet={srcSet}
        sizes={srcSet ? resolvedSizes : undefined}
        alt={alt}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={eager ? "high" : "low"}
        onLoad={() => setLoaded(true)}
        style={style}
        className={`${className} transition-opacity duration-500 ${
          eager || loaded ? "opacity-100" : "opacity-0"
        }`}
        {...rest}
      />
    </picture>
  );
};

export default ResponsiveImage;
